import { useEffect, useState } from "react";
import clsx from "clsx";
import { ShortTopArrow } from "../../assets/svg/ShortTopArrow";
import s from "./SceneHintOverlay.module.scss";
import { LongTopArrow } from "../../assets/svg/LongTopArrow";
import { ShortArrowBottom } from "../../assets/svg/ShortArrowBottom";
import { LeftLongArrow } from "../../assets/svg/LeftLongArrow";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import {
  disableSceneHint,
  setShowOverlayHint,
  toggleSceneHint,
} from "../../redux/reducers/app/appReducer";
import { CtrlToRotate } from "../../assets/svg/CtrlToRotate";
import {
  getIsShowOverlayHint,
  getIsToolbarReady,
} from "../../redux/reducers/app/appSelectors";
import { useURLRouting } from "../../utils/hooks/useURLRouting";

type HintConfig = {
  id: string;
  selector: string;
  text: string;
  align?: "top" | "bottom" | "left" | "right" | "upperTop";
  arrow?: ArrowStyle;
  type?: "tabs" | null;
};

type ArrowStyle =
  | "shortTop"
  | "shortBottom"
  | "longTop"
  | "longRight"
  | "longLeft";

const HINTS: HintConfig[] = [
  // High level areas
  {
    id: "rendering-area",
    selector: "[data-tour='rendering-area']",
    text: "Rendering Area",
    align: "bottom",
  },
  {
    id: "left-panel",
    selector: "[data-tour='left-panel']",
    text: "Tools, history, templates",
    align: "right",
  },

  // Top toolbar items (each one separate)
  {
    id: "toolbar-undo",
    selector: "[data-tour='toolbar-undo']",
    text: "Undo",
    align: "bottom",
    arrow: "shortTop",
  },
  {
    id: "toolbar-redo",
    selector: "[data-tour='toolbar-redo']",
    text: "Redo",
    align: "bottom",
    arrow: "longTop",
  },
  {
    id: "toolbar-move",
    selector: "[data-tour='toolbar-move']",
    text: "Move",
    align: "bottom",
    arrow: "shortTop",
  },
  {
    id: "toolbar-rotate",
    selector: "[data-tour='toolbar-rotate']",
    text: "Rotate",
    align: "bottom",
    arrow: "longTop",
  },
  {
    id: "toolbar-scale",
    selector: "[data-tour='toolbar-scale']",
    text: "Scale",
    align: "bottom",
    arrow: "shortTop",
  },
  {
    id: "toolbar-magnet",
    selector: "[data-tour='toolbar-magnet']",
    text: "Magnet",
    align: "bottom",
    arrow: "longTop",
  },
  {
    id: "toolbar-dimensions",
    selector: "[data-tour='toolbar-dimensions']",
    text: "Dimensions",
    align: "bottom",
    arrow: "shortTop",
  },
  {
    id: "toolbar-camera-settings",
    selector: "[data-tour='toolbar-camera-settings']",
    text: "Camera Settings",
    align: "bottom",
    arrow: "longTop",
  },
  {
    id: "toolbar-camera-preset",
    selector: "[data-tour='toolbar-camera-preset']",
    text: "Camera Preset",
    align: "bottom",
    arrow: "shortTop",
  },
  {
    id: "toolbar-projection",
    selector: "[data-tour='toolbar-projection']",
    text: "Projection",
    align: "bottom",
    arrow: "longTop",
  },

  // Sidebar Tabs.
  {
    id: "tab-enviroments",
    selector: "[data-tour='tab-enviroments']",
    text: "Enviroment Selection",
    align: "bottom",
    arrow: "shortTop",
    type: "tabs",
  },
  {
    id: "tab-objects",
    selector: "[data-tour='tab-objects']",
    text: "Product Selection",
    align: "bottom",
    arrow: "shortTop",
    type: "tabs",
  },

  // Sidebar bottom buttons.
  {
    id: "full-hd-button",
    selector: "[data-tour='full-hd-button']",
    text: "Render Resolution",
    align: "top",
    arrow: "shortBottom",
  },
  {
    id: "render-button",
    selector: "[data-tour='render-button']",
    text: "Create Render",
    align: "top",
    arrow: "shortBottom",
  },

  // Left bar buttons.
  {
    id: "templates-button",
    selector: "[data-tour='templates-btn']",
    text: "Templates",
    align: "left",
    arrow: "longLeft",
  },
  {
    id: "render-history-button",
    selector: "[data-tour='render-history-btn']",
    text: "Render History",
    align: "left",
    arrow: "longLeft",
  },
  {
    id: "reset-scene-button",
    selector: "[data-tour='reset-scene-btn']",
    text: "Reset Scene",
    align: "left",
    arrow: "longLeft",
  },
  {
    id: "control-sett-button",
    selector: "[data-tour='control-sett-btn']",
    text: "Control Settings Hint",
    align: "left",
    arrow: "longLeft",
  },
  {
    id: "logout-button",
    selector: "[data-tour='logout-btn']",
    text: "Log Out",
    align: "left",
    arrow: "longLeft",
  },

  // Upper top hotkeys.
  {
    id: "hotkeys-buttons",
    selector: "[data-tour='toolbar-move']",
    text: "",
    align: "upperTop",
    arrow: "longLeft",
  },
];

type HintPosition = {
  id: string;
  top: number;
  left: number;
  align: HintConfig["align"];
  arrow: ArrowStyle;
  type?: "tabs" | null;
};

const SceneHintOverlay = () => {
  const [positions, setPositions] = useState<HintPosition[]>([]);
  const dispatch = useAppDispatch();

  const isToolbarReady = useAppSelector(getIsToolbarReady());
  const isOverlayActive = useAppSelector(getIsShowOverlayHint());

  const { currentView } = useURLRouting();

  const isShowObjectItem =
    currentView && currentView.list_id && currentView.list_id >= "0";

  useEffect(() => {
    if (isOverlayActive) {
      document.body.classList.add("tour_active");
      dispatch(disableSceneHint({ isShow: false }));
    } else {
      document.body.classList.remove("tour_active");
    }

    return () => {
      document.body.classList.remove("tour_active");
    };
  }, [isOverlayActive]);

  useEffect(() => {
    const updatePositions = () => {
      const wrapper = document.querySelector(
        ".studio-wrapper"
      ) as HTMLElement | null;

      if (!wrapper) return;

      const parentRect = wrapper.getBoundingClientRect();

      const rects: HintPosition[] = HINTS.map(hint => {
        const el = document.querySelector(hint.selector) as HTMLElement | null;

        if (!el) {
          return {
            id: hint.id,
            top: 50,
            left: 50,
            align: hint.align,
            arrow: hint.arrow ?? "shortTop",
            type: hint.type ?? null,
          };
        }

        const r = el.getBoundingClientRect();

        const top =
          ((r.top + r.height / 2 - parentRect.top) / parentRect.height) * 100;
        const left =
          ((r.left + r.width / 2 - parentRect.left) / parentRect.width) * 100;

        return {
          id: hint.id,
          top,
          left,
          align: hint.align,
          arrow: hint.arrow ?? "shortTop",
          type: hint.type ?? null,
        };
      });

      setPositions(rects);
    };

    updatePositions();

    const wrapper = document.querySelector(".studio-wrapper") as Element | null;
    if (!wrapper) return;

    const resizeObserver = new ResizeObserver(updatePositions);
    resizeObserver.observe(wrapper);

    window.addEventListener("resize", updatePositions);

    return () => {
      window.removeEventListener("resize", updatePositions);
      resizeObserver.disconnect();
    };
  }, []);

  if (!positions.length) return null;

  return (
    <div className={`${s.tour_overlay} ${s.tour_active}`}>
      <div className={s.tour_overlay__backdrop} />

      {positions.map(pos => {
        const hint = HINTS.find(h => h.id === pos.id);

        return (
          <div
            key={pos.id}
            className={clsx(
              s.tour_hint,
              pos.align && s[`tour_hint--${pos.align}`]
            )}
            style={{
              top: `${pos.top}%`,
              left: `${pos.left}%`,
            }}
          >
            {pos.align === "bottom" &&
              isToolbarReady &&
              pos.type !== "tabs" && (
                <>
                  <div className={s.tour_hint__arrow}>
                    {pos.arrow === "shortTop" && <ShortTopArrow />}
                    {pos.arrow === "longTop" && <LongTopArrow />}

                    {pos.arrow === "shortBottom" && <ShortArrowBottom />}
                  </div>
                  <div className={s.tour_hint__card}>
                    <p>{hint?.text}</p>
                  </div>
                </>
              )}

            {pos.align === "bottom" &&
              !isShowObjectItem &&
              pos.type === "tabs" && (
                <>
                  <div className={s.tour_hint__arrow}>
                    {pos.arrow === "shortTop" && <ShortTopArrow />}
                    {pos.arrow === "longTop" && <LongTopArrow />}

                    {pos.arrow === "shortBottom" && <ShortArrowBottom />}
                  </div>
                  <div className={s.tour_hint__card}>
                    <p>{hint?.text}</p>
                  </div>
                </>
              )}

            {pos.align === "top" && (
              <>
                <div className={s.tour_hint__card}>
                  <p>{hint?.text}</p>
                </div>

                <div className={s.tour_hint__arrow}>
                  {pos.arrow === "shortBottom" && <ShortArrowBottom />}
                </div>
              </>
            )}

            {pos.align === "left" && (
              <>
                <div
                  className={clsx(
                    s.tour_hint__arrow,
                    pos.arrow === "longLeft" && s["tour_hint__arrow--long"]
                  )}
                >
                  {pos.arrow === "longLeft" && <LeftLongArrow />}
                </div>

                <div className={s.tour_hint__card}>
                  <p>{hint?.text}</p>
                </div>
              </>
            )}

            {pos.align === "upperTop" && (
              <>
                <div className={s.hotkeys_banner}>
                  <span className={s.hotkeys_banner__label}>Hotkeys</span>
                  <div className={s.hotkeys_banner__keys}>
                    {["1", "2", "3"].map(key => (
                      <span key={key} className={s.hotkeys_banner__pill}>
                        {key}
                      </span>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        );
      })}

      <div className={s.center_text}>
        <div>
          <CtrlToRotate fill="#fff" />
          <div>Select Move Tool + Hold Ctrl/Control to Move Gizmo</div>
        </div>
        <div>
          <CtrlToRotate fill="#fff" />
          <div>Select Rotate Tool + Hold Ctrl/Control to Rotate Gizmo</div>
        </div>
      </div>

      <button
        className={s.tour_overlay__cta}
        onClick={() => {
          dispatch(setShowOverlayHint(false));
          dispatch(toggleSceneHint());
        }}
      >
        Close
      </button>
    </div>
  );
};

export default SceneHintOverlay;
